import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface StoreConfig {
  name: string;
  phone: string;
  email: string;
  address: string;
  hours: string;
  hoursWeekdays: string;
  hoursFriday: string;
  // Marketing
  announcementText: string;
  showAnnouncement: boolean;
  // Financials
  deliveryPrice: number;
  freeShippingThreshold: number;
  // Club
  clubDiscountPercent: number;
  // Social
  instagramLink: string;
  facebookLink: string;
  tiktokLink: string;
}

const DEFAULT_STORE_CONFIG: StoreConfig = {
  name: 'FLORISIMO',
  phone: '972532157572',
  email: 'orders@florisimo.co.il',
  address: 'יהודי פקיעין, חדרה',
  hours: "א'-ה' 9:00-20:00",
  hoursWeekdays: '09:00-20:00',
  hoursFriday: '09:00-14:00',
  // Marketing
  announcementText: '',
  showAnnouncement: false,
  // Financials
  deliveryPrice: 50,
  freeShippingThreshold: 300,
  // Club
  clubDiscountPercent: 10,
  // Social
  instagramLink: '',
  facebookLink: '',
  tiktokLink: '',
};

interface StoreConfigContextType {
  storeConfig: StoreConfig;
  loading: boolean;
  refetch: () => Promise<void>;
}

const StoreConfigContext = createContext<StoreConfigContextType>({
  storeConfig: DEFAULT_STORE_CONFIG,
  loading: true,
  refetch: async () => {},
});

export function useStoreConfig() {
  return useContext(StoreConfigContext);
}

interface StoreConfigProviderProps {
  children: ReactNode;
}

export function StoreConfigProvider({ children }: StoreConfigProviderProps) {
  const [storeConfig, setStoreConfig] = useState<StoreConfig>(DEFAULT_STORE_CONFIG);
  const [loading, setLoading] = useState(true);

  const fetchStoreConfig = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'store_config')
        .single();

      if (data?.value) {
        const value = data.value as Record<string, unknown>;
        setStoreConfig({
          name: (value.name as string) || DEFAULT_STORE_CONFIG.name,
          phone: (value.phone as string) || DEFAULT_STORE_CONFIG.phone,
          email: (value.email as string) || DEFAULT_STORE_CONFIG.email,
          address: (value.address as string) || DEFAULT_STORE_CONFIG.address,
          hours: (value.hours as string) || DEFAULT_STORE_CONFIG.hours,
          hoursWeekdays: (value.hoursWeekdays as string) || DEFAULT_STORE_CONFIG.hoursWeekdays,
          hoursFriday: (value.hoursFriday as string) || DEFAULT_STORE_CONFIG.hoursFriday,
          // Marketing
          announcementText: (value.announcementText as string) || DEFAULT_STORE_CONFIG.announcementText,
          showAnnouncement: typeof value.showAnnouncement === 'boolean' ? value.showAnnouncement : DEFAULT_STORE_CONFIG.showAnnouncement,
          // Financials
          deliveryPrice: typeof value.deliveryPrice === 'number' ? value.deliveryPrice : DEFAULT_STORE_CONFIG.deliveryPrice,
          freeShippingThreshold: typeof value.freeShippingThreshold === 'number' ? value.freeShippingThreshold : DEFAULT_STORE_CONFIG.freeShippingThreshold,
          // Club
          clubDiscountPercent: typeof value.clubDiscountPercent === 'number' ? value.clubDiscountPercent : DEFAULT_STORE_CONFIG.clubDiscountPercent,
          // Social
          instagramLink: (value.instagramLink as string) || DEFAULT_STORE_CONFIG.instagramLink,
          facebookLink: (value.facebookLink as string) || DEFAULT_STORE_CONFIG.facebookLink,
          tiktokLink: (value.tiktokLink as string) || DEFAULT_STORE_CONFIG.tiktokLink,
        });
      }
    } catch (error) {
      console.error('Error fetching store config:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStoreConfig();
  }, [fetchStoreConfig]);

  return (
    <StoreConfigContext.Provider value={{ storeConfig, loading, refetch: fetchStoreConfig }}>
      {children}
    </StoreConfigContext.Provider>
  );
}
